import { z } from 'zod'
import { instantSchema } from '../validators/temporal.validator.js'
import { apiTokenScopeSchema } from '../enums/api-token-scope.enum.js'

/**
 * One of a person's API tokens, as the listing shows it.
 *
 * The secret is not here and cannot be: only its SHA-256 digest is stored, so
 * after creation there is nothing to return. `prefix` is the visible head of the
 * token, which is enough to tell one from another and is not a secret.
 */
export const apiTokenDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** The token's visible head, e.g. `cse_live_7Fq2…`. */
  prefix: z.string(),
  /** When set, the token may only make GET requests, whatever the grants say. */
  readOnly: z.boolean(),
  /**
   * What the token may touch. Carried in the listing because a token limited to
   * one area, shown as "full access", would misdescribe the thing it is asking
   * someone to trust.
   */
  scopes: z.array(z.object({ scope: apiTokenScopeSchema, write: z.boolean() })),
  /** Permits the bulk irreversible routes, on top of write on their scope. */
  allowDestructive: z.boolean(),
  /** Null means it never expires. */
  expiresAt: instantSchema.nullable(),
  /** Null until it is first used; written at most once a minute after that. */
  lastUsedAt: instantSchema.nullable(),
  /** Set once withdrawn. A revoked token is kept so the listing can show it went. */
  revokedAt: instantSchema.nullable(),
  /** False once it has expired or been revoked — what the listing colors on. */
  active: z.boolean(),
  /**
   * How many changes it has made through MCP tools. Defaults to none so a page
   * newer than the API still reads the listing.
   */
  changeCount: z.number().int().nonnegative().default(0),
  createdAt: instantSchema,
})
export type ApiTokenDto = z.infer<typeof apiTokenDtoSchema>

/**
 * A token the moment it is made — the only time the secret exists on the wire.
 *
 * Separate from {@link apiTokenDtoSchema} on purpose: a type that sometimes
 * carries the secret would be a type nobody could tell was safe to log.
 */
export const createdApiTokenDtoSchema = z.object({
  token: apiTokenDtoSchema,
  /** The raw token. Shown once, never stored, never returned again. */
  secret: z.string().min(1),
})
export type CreatedApiTokenDto = z.infer<typeof createdApiTokenDtoSchema>

export const apiTokenListDtoSchema = z.object({
  tokens: z.array(apiTokenDtoSchema),
})
export type ApiTokenListDto = z.infer<typeof apiTokenListDtoSchema>
